import React, { memo, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, Icon, LoadingLabel } from '@alycecom/ui';
import classNames from 'classnames';
import { fakeItemsFactory } from '@alycecom/utils';

import { ITagData } from '../../store/tags/tags.types';

const useStyles = makeStyles<AlyceTheme>(({ palette, breakpoints, spacing }) => ({
  tagsList: {
    display: 'flex',
    flexWrap: 'wrap',
    width: 755,
    height: '100%',
    paddingBottom: spacing(3),

    [breakpoints.down('lg')]: {
      width: 399,
    },
    [breakpoints.down('sm')]: {
      width: 266,
    },
    [breakpoints.down(370)]: {
      width: 133,
    },
  },
  tagItem: {
    width: 127,
    marginLeft: spacing(1.5),
    marginRight: spacing(1.5),
    cursor: 'pointer',

    [breakpoints.down('lg')]: {
      width: 117,
      marginLeft: spacing(1),
      marginRight: spacing(1),
    },
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: 127,
    marginBottom: 3,

    [breakpoints.down('lg')]: {
      height: 'auto',
    },
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
  },
  checkmark: {
    position: 'absolute',
    width: 48,
    height: 48,
    left: 'calc(50% - 24px)',
    top: 'calc(50% - 24px)',
    borderRadius: 48,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: palette.link.main,
    '& svg': {
      color: palette.common.white,
      width: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export interface ITagsListProps {
  tags: ITagData[];
  selected: number[];
  onChangeSelected: (selected: number[]) => void;
  isLoading: boolean;
}

const TagsList = ({ tags, selected, onChangeSelected, isLoading }: ITagsListProps) => {
  const classes = useStyles();

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const handleToggleSelected = useCallback(
    (tagId: number) => {
      if (selectedSet.has(tagId)) {
        onChangeSelected(selected.filter(id => id !== tagId));
      } else {
        onChangeSelected([...selected, tagId]);
      }
    },
    [onChangeSelected, selected, selectedSet],
  );

  const items = useMemo(() => fakeItemsFactory(tags, isLoading, id => ({ tagId: id } as ITagData), 20), [
    tags,
    isLoading,
  ]);

  return (
    <Box className={classes.tagsList}>
      {items.map(({ tagId, tagName, image }) => (
        <Box
          onClick={() => handleToggleSelected(tagId)}
          key={tagId}
          mb={3}
          className={classes.tagItem}
          data-testid={`DeclaredData.Tag.${tagId}`}
        >
          <Box className={classes.imageWrapper}>
            {isLoading ? (
              <LoadingLabel width="100%" height="100%" />
            ) : (
              <img src={image} alt={tagName} className={classes.image} />
            )}
            {selectedSet.has(tagId) && !isLoading && (
              <Box className={classes.checkmark} data-testid={`DeclaredData.Tag.${tagId}.Checked`}>
                <Icon icon="check" />
              </Box>
            )}
          </Box>
          <Typography
            className={classNames(classes.name, 'Body-Regular-Left-Static')}
            data-testid={`DeclaredData.Tag.${tagId}.Name`}
          >
            {isLoading ? <LoadingLabel mt="6px" /> : tagName}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default memo(TagsList);
